import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ['form', 'attribute', 'range', 'priceDisplay', 'simulated'];
  static values = { unavailableText: String };

  // Page load fires several requests at once (connect, the sliders initialising the hidden coverage
  // field): they are collapsed into one, and a response only updates the display if it answers the
  // latest request — an earlier one can land last and show a price the form no longer says.
  static DEBOUNCE_MS = 150;

  connect() {
    this.attributeTargets.forEach((attribute) => {
      attribute.addEventListener('input', this.getPrice);
    });

    this.rangeTargets.forEach((range) => {
      range.addEventListener('range-jump', this.getPrice);
    });

    this.getPrice();
  }

  disconnect() {
    clearTimeout(this.debounceTimer);
    this.attributeTargets.forEach((attribute) => {
      attribute.removeEventListener('input', this.getPrice);
    });

    this.rangeTargets.forEach((range) => {
      range.removeEventListener('range-jump', this.getPrice);
    });
  }

  getPrice = () => {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(this.fetchPrice, this.constructor.DEBOUNCE_MS);
  }

  fetchPrice = () => {
    const requestId = (this.latestRequestId = (this.latestRequestId || 0) + 1);
    const form = this.formTarget
    form.action.replace(/steps.*/, 'get_price_from_form');
    const getPriceForm = form.cloneNode(true);
    getPriceForm.action = form.action.replace(/steps.*/, 'get_price_from_form');
    const csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute("content");
    const unavailableText = this.unavailableTextValue || '—';

    const updatePriceDisplay = (price, simulation) => {
      if (requestId !== this.latestRequestId) return;
      this.priceDisplayTargets.forEach((priceDisplayElement) => {
        priceDisplayElement.textContent = price;
      });
      // Optional: products that simulate the yearly and monthly prices (one amount per data-simulation-key).
      this.simulatedTargets.forEach((element) => {
        element.textContent = simulation?.[element.dataset.simulationKey] ?? unavailableText;
      });
    }

    // Handle form submission asynchronously
    async function submitFormAsync(getPriceForm) {
      try {
        const formData = new FormData(getPriceForm);
        const response = await fetch(getPriceForm.action, {
          method: getPriceForm.method,
          body: formData,
          headers: {
            "X-CSRF-Token": csrfToken
          }
        });

        if (response.ok) {
          // Handle successful response
          const responseData = await response.json();
          updatePriceDisplay(responseData.price, responseData.simulation)
        } else {
          console.error("Price request failed.", response.status);
          updatePriceDisplay(unavailableText);
        }
      } catch (error) {
        console.error("An error occurred during form submission:", error);
        updatePriceDisplay(unavailableText);
      }
    }

    submitFormAsync(getPriceForm);
  }
}
